import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';
import { lockBodyScroll, unlockBodyScroll, resetBodyScrollLock } from '@theme/scroll-lock';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {boolean} */
  #isScrollLocked = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    window.addEventListener('pagehide', this.#cleanupDrawerState);
    window.addEventListener('pageshow', this.#cleanupDrawerState);
    this.#setupAnimatedElementListeners();

    // A visitor can tap the hamburger before this custom element finishes
    // upgrading — the native <details> toggle opens the drawer instantly. If we
    // unconditionally ran #cleanupDrawerState() here, that upgrade would
    // immediately force the just-opened drawer shut, which looks like the menu
    // "closing itself" on early clicks. So instead: if the drawer is already
    // open at upgrade time, adopt that state properly (scroll lock, focus trap,
    // menu-open class); otherwise clear any stale lock/focus state as before.
    if (this.isOpen) {
      this.open();
    } else {
      this.#cleanupDrawerState();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('pagehide', this.#cleanupDrawerState);
    window.removeEventListener('pageshow', this.#cleanupDrawerState);
    this.#cleanupDrawerState();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();

    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    event?.preventDefault();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Lock the page scroll only when the top-level drawer (not a submenu) is
    // opening. This uses the same shared `position: fixed`-based lock as
    // every other drawer/dialog in the theme (cart, search, quick add,
    // filters, zoom gallery) — see assets/scroll-lock.js for why that
    // technique is used instead of plain `overflow: hidden`.
    if (details === this.refs.details && !this.#isScrollLocked) {
      this.#isScrollLocked = true;
      requestAnimationFrame(() => {
        lockBodyScroll();
      });
    }

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    this.#openDefaultMenuItems();

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      this.#onAnimationEndOrTimeout(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Release the focus trap immediately so keyboard focus returns to the
    // trigger without waiting on the closing animation.
    if (details === this.refs.details) {
      removeTrapFocus();
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    this.#onAnimationEndOrTimeout(
      drawer || details,
      () => {
        // The scroll lock is released only once the drawer has actually
        // finished animating off-screen. Releasing it earlier would leave
        // the page scrollable while the drawer is still visible, letting a
        // touch/scroll during the close animation move the page to a
        // different position than the one that gets restored here.
        if (details === this.refs.details) {
          this.#unlockBodyScroll();
        }

        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach((openDetail) => {
            if (!openDetail.hasAttribute('data-menu-drawer-default-open')) reset(openDetail);
          });
          this.#openDefaultMenuItems();
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Same as onAnimationEnd, but guarantees the callback runs within `timeoutMs`
   * even if the underlying animation promise never settles (e.g. the animation
   * gets interrupted or cancelled by a rapid re-open/re-close, or the browser
   * never fires an end event for some other reason). Without this safety net,
   * a stalled animation can permanently skip focus-trap cleanup and freeze
   * interaction with the rest of the page.
   *
   * @param {Element | Element[]} elements
   * @param {() => void} callback
   * @param {Object} [options]
   * @param {number} [timeoutMs]
   */
  #onAnimationEndOrTimeout(elements, callback, options = { subtree: true }, timeoutMs = 500) {
    let didRun = false;
    const runOnce = () => {
      if (didRun) return;
      didRun = true;
      callback();
    };

    onAnimationEnd(elements, runOnce, options);
    setTimeout(runOnce, timeoutMs);
  }

  /**
   * Reverses the scroll lock applied in `open()` and restores the page to
   * its prior scroll position.
   */
  #unlockBodyScroll() {
    if (!this.#isScrollLocked) return;

    this.#isScrollLocked = false;
    unlockBodyScroll();
  }

  /**
   * Clear drawer lock/focus state when navigating away, restoring from bfcache,
   * or disconnecting. This prevents a stale drawer focus trap from freezing the
   * next page if navigation happens while the menu is open.
   */
  #cleanupDrawerState = () => {
    removeTrapFocus();
    if (this.#isScrollLocked) {
      this.#isScrollLocked = false;
      resetBodyScrollLock();
    }
    this.querySelectorAll('details[open]').forEach((details) => {
      if (!details.hasAttribute('data-menu-drawer-default-open')) reset(details);
    });
    this.#openDefaultMenuItems();
    this.refs.menuDrawer?.classList.remove('menu-drawer--has-submenu-opened');
  };

  #openDefaultMenuItems = () => {
    this.querySelectorAll('details[data-menu-drawer-default-open]').forEach((details) => {
      details.setAttribute('open', '');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    });
  };

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
